import { baseApi } from './baseApi';

export interface OrderEvent {
  id: string;
  orderId: string;
  eventType: string;
  eventData: string; // JSON string
  userId: string;
  userName: string;
  eventTimestamp: string;
  tableId?: string;
}

export interface OrderEventSummary {
  orderId: string;
  orderNumber: number;
  tableNumber: string;
  orderOpenTime: string;
  orderCloseTime?: string | null;
  totalEvents: number;
  firstEventTimestamp?: string | null;
  lastEventTimestamp?: string | null;
  events: OrderEvent[];
  eventTypes: string[];
  usersInvolved: string[];
  eventTypeCounts: Record<string, number>;
}

export interface OrderEventFilter {
  orderId?: string;
  tableId?: string;
  userId?: string;
  eventType?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateOrderEventRequest {
  orderId: string;
  eventType: string;
  eventData: any;
  tableId?: string;
}

export interface EventAnalytics {
  eventTypeCounts: Record<string, number>;
  totalEvents: number;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

export interface UserActivity {
  userId: string;
  userName: string;
  eventCounts: Record<string, number>;
  totalEvents: number;
  lastActivity: string;
}

export interface TableActivity {
  tableId: string;
  tableNumber: string;
  eventCounts: Record<string, number>;
  totalEvents: number;
  lastActivity: string;
}

export const orderEventsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get specific order event
    getOrderEvent: builder.query<OrderEvent, string>({
      query: (eventId) => `/orderevents/${eventId}`,
      providesTags: (_result, _error, eventId) => [{ type: 'OrderEvent', id: eventId }],
    }),

    // Search order events with filters
    searchOrderEvents: builder.mutation<{ events: OrderEvent[]; totalCount: number; totalPages: number }, OrderEventFilter>({
      query: (filter) => ({
        url: '/orderevents/search',
        method: 'POST',
        body: filter,
      }),
      invalidatesTags: ['OrderEvent'],
    }),

    // Get order event summary
    getOrderEventSummary: builder.query<OrderEventSummary, string>({
      query: (orderId) => `/orderevents/summary/${orderId}`,
      providesTags: (_result, _error, orderId) => [{ type: 'OrderEvent', id: `summary-${orderId}` }],
    }),

    // Create custom order event
    createOrderEvent: builder.mutation<OrderEvent, CreateOrderEventRequest>({
      query: (newEvent) => ({
        url: '/orderevents',
        method: 'POST',
        body: newEvent,
      }),
      invalidatesTags: ['OrderEvent'],
    }),

    // Get event analytics
    getEventAnalytics: builder.query<EventAnalytics, { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate }) => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        return `/orderevents/analytics/event-counts?${params.toString()}`;
      },
      providesTags: ['OrderEvent'],
    }),

    // Get user activity
    getUserActivity: builder.query<UserActivity, { userId: string; startDate?: string; endDate?: string }>({
      query: ({ userId, startDate, endDate }) => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        return `/orderevents/analytics/user-activity/${userId}?${params.toString()}`;
      },
      providesTags: (_result, _error, { userId }) => [{ type: 'OrderEvent', id: `user-${userId}` }],
    }),

    // Get table activity
    getTableActivity: builder.query<TableActivity, { tableId: string; startDate?: string; endDate?: string }>({
      query: ({ tableId, startDate, endDate }) => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        return `/orderevents/analytics/table-activity/${tableId}?${params.toString()}`;
      },
      providesTags: (_result, _error, { tableId }) => [{ type: 'OrderEvent', id: `table-${tableId}` }],
    }),

    // Get available event types
    getEventTypes: builder.query<string[], void>({
      query: () => '/orderevents/event-types',
      providesTags: ['OrderEvent'],
    }),

    // Get event count for order
    getOrderEventCount: builder.query<number, string>({
      query: (orderId) => `/orderevents/count/${orderId}`,
      providesTags: (_result, _error, orderId) => [{ type: 'OrderEvent', id: `count-${orderId}` }],
    }),

    // Check if order has specific event type
    hasOrderEvent: builder.query<boolean, { orderId: string; eventType: string }>({
      query: ({ orderId, eventType }) => `/orderevents/has-event/${orderId}/${eventType}`,
      providesTags: (_result, _error, { orderId, eventType }) => [
        { type: 'OrderEvent', id: `has-${orderId}-${eventType}` }
      ],
    }),

    // Get last event timestamp for order
    getLastEventTimestamp: builder.query<string | null, string>({
      query: (orderId) => `/orderevents/last-event/${orderId}`,
      providesTags: (_result, _error, orderId) => [{ type: 'OrderEvent', id: `last-${orderId}` }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetOrderEventQuery,
  useSearchOrderEventsMutation,
  useGetOrderEventSummaryQuery,
  useCreateOrderEventMutation,
  useGetEventAnalyticsQuery,
  useGetUserActivityQuery,
  useGetTableActivityQuery,
  useGetEventTypesQuery,
  useGetOrderEventCountQuery,
  useHasOrderEventQuery,
  useGetLastEventTimestampQuery,
} = orderEventsApi;